import { Column, Header } from '@tanstack/react-table';
import { CSSProperties, ReactNode, useState } from 'react';
import ArrowDownwardIcon from '../../assets/icons/arrow_downward.svg?react';
import ArrowUpwardIcon from '../../assets/icons/arrow_upward.svg?react';
import SwapVertIcon from '../../assets/icons/swap_vert.svg?react';
import { cn } from '../../utils/className';
import { TableHead } from './Table';

type SortDirection = 'asc' | 'desc' | false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface SortableTableHeadProps<TData = any> {
  column: Column<TData, unknown>;
  header: Header<TData, unknown>;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onSortChange?: (columnId: string, direction: SortDirection) => void;
  /**
   * Optional filter element to render in the header (e.g., TableColumnFilter)
   */
  filterElement?: ReactNode;
  /** Hide the right border (useful for selection columns) */
  noBorder?: boolean;
  /** Whether to show resize handle on this column */
  canResize?: boolean;
  /** Fit the column to its content (double-click on the resize handle) */
  onAutoFit?: () => void;
}

export function SortableTableHead<TData>({
  column,
  header,
  children,
  className,
  style,
  onSortChange,
  filterElement,
  noBorder,
  canResize = false,
  onAutoFit,
}: SortableTableHeadProps<TData>) {
  const [isHovered, setIsHovered] = useState(false);
  const isSorted = column.getIsSorted();
  const canSort = column.getCanSort();
  const isResizing = header.column.getIsResizing();

  if (!canSort) {
    return (
      <TableHead
        className={cn('relative', className)}
        style={style}
        noBorder={noBorder}
        data-column-id={column.id}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className='flex w-full items-center justify-between gap-2'>
          <span className='flex items-center'>{children}</span>

          {/* Filter element (if provided) */}
          {filterElement && (
            <div className='flex items-center'>{filterElement}</div>
          )}
        </div>

        {/* Resize handle */}
        {canResize && (
          <ResizeHandle
            header={header}
            isResizing={isResizing}
            isVisible={isHovered || isResizing}
            onAutoFit={onAutoFit}
          />
        )}
      </TableHead>
    );
  }

  const handleClick = () => {
    if (!canSort) return;

    let nextSort: SortDirection = 'asc';

    // Cycle through: unsorted -> asc -> desc -> unsorted
    if (!isSorted) {
      nextSort = 'asc';
    } else if (isSorted === 'asc') {
      nextSort = 'desc';
    } else if (isSorted === 'desc') {
      nextSort = false;
    }

    // Update TanStack Table state
    if (nextSort === false) {
      // Clear sorting for this column
      column.clearSorting();
    } else {
      // Set sorting (true = desc, false = asc)
      column.toggleSorting(nextSort === 'desc', false);
    }

    // Notify parent if callback provided
    if (onSortChange) {
      onSortChange(column.id, nextSort);
    }
  };

  return (
    <TableHead
      className={cn('group relative select-none', className)}
      style={style}
      noBorder={noBorder}
      data-column-id={column.id}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='flex w-full min-w-0 items-center justify-between gap-2'>
        <span className='min-w-0 truncate'>{children}</span>

        {/* Right side: sort indicator + filter */}
        <div className='flex shrink-0 items-center gap-1'>
          {/* Sort control - only this button triggers sorting */}
          <button
            type='button'
            onClick={handleClick}
            aria-label={`Sort by ${column.id}`}
            aria-pressed={Boolean(isSorted)}
            className='focus-visible:outline-focus flex cursor-pointer
              items-center rounded p-1 focus-visible:outline'
          >
            <div
              className={cn(
                'transition-opacity',
                !isSorted && !isHovered && 'opacity-0'
              )}
            >
              {!isSorted && <SortIcon direction={null} />}
              {isSorted === 'asc' && <SortIcon direction='asc' />}
              {isSorted === 'desc' && <SortIcon direction='desc' />}
            </div>
          </button>

          {/* Filter element (if provided) */}
          {filterElement}
        </div>
      </div>

      {/* Resize handle */}
      {canResize && (
        <ResizeHandle
          header={header}
          isResizing={isResizing}
          isVisible={isHovered || isResizing}
          onAutoFit={onAutoFit}
        />
      )}
    </TableHead>
  );
}

interface SortIconProps {
  direction: 'asc' | 'desc' | null;
}

function SortIcon({ direction }: SortIconProps) {
  // Unsorted state (on hover): show swap_vert in tertiary color
  if (direction === null) {
    return (
      <SwapVertIcon
        className='text-text-tertiary h-4 w-4 [&_path]:fill-current
          [&_svg]:fill-current'
      />
    );
  }

  // Ascending: show active up arrow in focus color
  if (direction === 'asc') {
    return (
      <ArrowUpwardIcon
        className='text-surface-focus h-4 w-4 [&_path]:fill-current
          [&_svg]:fill-current'
      />
    );
  }

  // Descending: show active down arrow in focus color
  return (
    <ArrowDownwardIcon
      className='text-surface-focus h-4 w-4 [&_path]:fill-current
        [&_svg]:fill-current'
    />
  );
}

/**
 * Draggable resize handle for column headers.
 * Shows on hover and uses TanStack Table's built-in resize handler.
 */
interface ResizeHandleProps<TData = unknown> {
  header: Header<TData, unknown>;
  isResizing: boolean;
  isVisible: boolean;
  /** Fit the column to its content; falls back to resetting the size */
  onAutoFit?: () => void;
}

function ResizeHandle<TData>({
  header,
  isResizing,
  isVisible,
  onAutoFit,
}: ResizeHandleProps<TData>) {
  return (
    <div
      onDoubleClick={() =>
        onAutoFit ? onAutoFit() : header.column.resetSize()
      }
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      className={cn(
        `absolute top-0 right-0 h-full w-1 cursor-col-resize touch-none
        select-none`,
        'bg-border-secondary hover:bg-surface-focus transition-colors',
        isResizing && 'bg-surface-focus',
        isVisible ? 'opacity-100' : 'opacity-0'
      )}
      aria-label='Resize column'
    />
  );
}
